<template>
  <Transition name="modal-fade">
    <div
      v-if="modelValue"
      class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm"
    >
      <Transition name="modal-scale">
        <div
          v-if="modelValue"
          style="max-width: 420px !important;"
          class="bg-surface border border-border rounded-xl shadow-2xl w-full mx-4 animate-in fade-in zoom-in-95 duration-200"
          @click.stop
        >
          <!-- Header -->
          <div class="px-6 py-4 border-b border-border">
            <h3 class="text-lg font-semibold text-text-main">{{ title }}</h3>
            <p v-if="message" class="text-sm text-text-muted mt-1">{{ message }}</p>
          </div>

          <!-- Input -->
          <div class="px-6 py-4">
            <input
              ref="inputRef"
              v-model="inputValue"
              type="text"
              class="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-text-main placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
              :placeholder="placeholder"
              @keyup.enter="handleConfirm"
            />
          </div>

          <!-- Footer -->
          <div class="flex items-center justify-end gap-3 px-6 py-4 border-t border-border">
            <button
              @click="handleCancel"
              class="px-4 py-2 text-sm font-medium text-text-main bg-background border border-border rounded-lg hover:bg-hover transition-colors"
            >
              {{ cancelText }}
            </button>
            <button
              @click="handleConfirm"
              class="px-4 py-2 text-sm font-medium text-white bg-brand rounded-lg hover:opacity-90 transition-all"
            >
              {{ confirmText }}
            </button>
          </div>
        </div>
      </Transition>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { ref, watch, nextTick } from 'vue'

interface Props {
  modelValue: boolean
  title: string
  message?: string
  placeholder?: string
  defaultValue?: string
  confirmText?: string
  cancelText?: string
}

interface Emits {
  (e: 'update:modelValue', value: boolean): void
  (e: 'confirm', value: string): void
  (e: 'cancel'): void
}

const props = withDefaults(defineProps<Props>(), {
  defaultValue: '',
  confirmText: '确认',
  cancelText: '取消',
  placeholder: ''
})

const emit = defineEmits<Emits>()

const inputValue = ref(props.defaultValue)
const inputRef = ref<HTMLInputElement>()

watch(() => props.modelValue, (val) => {
  if (val) {
    inputValue.value = props.defaultValue
    nextTick(() => inputRef.value?.focus())
  }
})

const handleConfirm = () => {
  if (inputValue.value.trim()) {
    emit('confirm', inputValue.value.trim())
    emit('update:modelValue', false)
  }
}

const handleCancel = () => {
  emit('cancel')
  emit('update:modelValue', false)
}
</script>

<style scoped>
.modal-fade-enter-active,
.modal-fade-leave-active { transition: opacity 0.2s ease; }
.modal-fade-enter-from,
.modal-fade-leave-to { opacity: 0; }
.modal-scale-enter-active,
.modal-scale-leave-active { transition: all 0.2s ease; }
.modal-scale-enter-from,
.modal-scale-leave-to { opacity: 0; transform: scale(0.95); }
</style>
